import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CustomTextField from "../../../components/common/Textfield";
import {
  fetchSites,
  SiteListApiItem,
} from "../site-listings/services/siteListingConfig";
import WebsiteCard from "./components/websites/WebsiteCard";
import { useSiteSeoStats } from "./hooks/useSiteSeoStats";

const SEOManagement: React.FC = () => {
  const navigate = useNavigate();
  const [sites, setSites] = useState<SiteListApiItem[]>([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [websiteSearch, setWebsiteSearch] = useState("");

  const siteStats = useSiteSeoStats(sites);

  useEffect(() => {
    const loadSites = async () => {
      try {
        const response = await fetchSites();
        setSites(response.data.results || []);
      } catch (error) {
        console.error("Failed to load websites", error);
        toast.error("Failed to load websites");
      } finally {
        setLoadingSites(false);
      }
    };
    loadSites();
  }, []);

  if (loadingSites) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredSites = sites.filter((site) =>
    `${site.name || ""} ${site.domain || ""}`
      .toLowerCase()
      .includes(websiteSearch.toLowerCase()),
  );

  return (
    <Box sx={{ pb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={800}>
          SEO Management
        </Typography>
        <Typography color="text.secondary">
          Select a website to manage the SEO pages it contains.
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        <CustomTextField
          size="small"
          placeholder="Search websites..."
          value={websiteSearch}
          onChange={(event) => setWebsiteSearch(event.target.value)}
          sx={{ maxWidth: 440 }}
        />
        <Chip
          label={`${sites.length} website${sites.length === 1 ? "" : "s"}`}
        />
      </Box>
      {!filteredSites.length ? (
        <Paper
          variant="outlined"
          sx={{ p: 6, borderRadius: 3, textAlign: "center" }}
        >
          <Typography color="text.secondary">No websites found.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2.5}>
          {filteredSites.map((site) => (
            <Grid key={site.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <WebsiteCard
                site={site}
                stats={siteStats[site.id]}
                onClick={() =>
                  navigate(`/configurations/seo-management/${site.id}`)
                }
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default SEOManagement;
